import { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { 
    Box, 
    Typography, 
    Grid, 
    Container, 
    IconButton,
} from '@mui/material';
import FeedIcon from '@mui/icons-material/Feed';
import SchoolIcon from '@mui/icons-material/School';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import CloseIcon from '@mui/icons-material/Close';
import { STPButton } from '../components/Botones';
import { getWelcomeCourses } from '../services/getWelcomeCourses';
import CourseDetailDialog from '../components/CourseDetailDialog';
import { useTranslation } from 'react-i18next';

const anunciosImages = import.meta.glob('../assets/anuncios/*.jpg', { eager: true });

export const imageMap = import.meta.glob('../assets/images/*.{jpg,png}', { eager: true, import: 'default' });

function getImageUrlFromDb(pathFromDb) {
    const key = `../assets/images/${pathFromDb}`;
    return imageMap[key] || '/placeholder.png';
  }

// Componente de curso
const CourseItem = ({ course, isReverse, onClick }) => {
    return (
        <Box 
            sx={{ 
                display: 'flex', 
                mb: 3,
                justifyContent: 'center',
                width: '100%',
                cursor: 'pointer',
                '&:hover': {
                    opacity: 0.9,
                    transform: 'scale(1.02)',
                    transition: 'all 0.3s'
                },
            }}
            onClick={() => onClick(course)}
        >
            <Box sx={{ 
                display: 'flex', 
                flexDirection: isReverse ? 'row-reverse' : 'row',
                width: '100%',
                maxWidth: '350px',
                justifyContent: 'flex-start',
                alignItems: 'center'
            }}>
                <Box sx={{ 
                    flex: 2,
                    flexShrink: 0,
                    [isReverse ? 'ml' : 'mr']: 2
                }}>
                    <img 
                        src={getImageUrlFromDb(course.image)}
                        alt={`${course.title} students`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', border: '3px solid #50A579' }} 
                    />
                </Box>
                <Box sx={{ textAlign: isReverse ? 'right' : 'left', flex: 3 }}>
                    <Typography 
                        variant="subtitle1" 
                        sx={{ 
                            fontWeight: 'bold', 
                            fontSize: '1rem', 
                            color: '#50A579', 
                            mt: 1,
                            textAlign: isReverse ? 'right' : 'left'
                        }}
                    >
                        {course.title}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                        {course.hoursPerWeek}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                        {course.ageRequirement}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

const Welcome = ( { setIsAuthenticated }) => {
    const { t, i18n } = useTranslation();
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [showPromo, setShowPromo] = useState(true);
    const [welcomeGroups, setWelcomeGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [randomAnuncio, setRandomAnuncio] = useState('');

    // Efecto para controlar el scroll del body
    // useEffect(() => {
    //     if (showPromo) {
    //         document.body.style.overflow = 'hidden';
    //     } else {
    //         document.body.style.overflow = 'unset';
    //     }
    //     return () => {
    //         document.body.style.overflow = 'unset';
    //     };
    // }, [showPromo]);

    // Efecto para seleccionar una imagen aleatoria de anuncios
    // useEffect(() => {
    //     // Convertir el objeto de imágenes a un array
    //     const anuncios = Object.values(anunciosImages).map(module => module.default);
        
    //     // Si hay anuncios disponibles, seleccionar uno aleatoriamente
    //     if (anuncios.length > 0) {
    //         const randomIndex = Math.floor(Math.random() * anuncios.length);
    //         setRandomAnuncio(anuncios[randomIndex]);
    //     }
    // }, []);

    useEffect(() => {
        setLoading(true);
        getWelcomeCourses()
          .then(groups => {
            const mappedGroups = groups.map(g => {
              let age;
              if (g.edad_recomendada_ci == null && g.edad_recomendada_cs != null) {
                age = t('courseDetail.ageFormats.lessThan', { age: g.edad_recomendada_cs });
              } else if (g.edad_recomendada_ci != null && g.edad_recomendada_cs == null) {
                age = t('courseDetail.ageFormats.moreThan', { age: g.edad_recomendada_ci });
              } else if (g.edad_recomendada_ci === g.edad_recomendada_cs) {
                age = t('courseDetail.ageFormats.exactAge', { age: g.edad_recomendada_ci });
              } else {
                age = t('courseDetail.ageFormats.ageRange', { minAge: g.edad_recomendada_ci, maxAge: g.edad_recomendada_cs });
              }
              return {
                id: g.id,
                title: g.nombre,
                image: g.imagen_path,
                ageRequirement: age,
                material: g.material,
                descripcion: i18n.language === 'ca_ES' ? g.descripcion : g.descripcion_es,
                cursos: g.cursos
              };
            });
                
            setWelcomeGroups(mappedGroups);
            setLoading(false);
          })
          .catch(error => {
            console.error('Error al obtener los cursos de bienvenida', error);
          });
        setLoading(false);
    }, [t, i18n.language]);

    const handleCourseClick = (group) => {
        setSelectedGroup(group);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const handleGoTestNivell = () => {
        window.open('https://www.vilatraining.cat/tests-de-nivell/', '_blank', 'noopener');
    };

    const handleClosePromo = () => {
        setShowPromo(false);
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            width: '100%',
            position: 'relative',
        }}
        className="home"
        >
            {/* Contenido principal */}
            <Box sx={{ position: 'relative', width: '100%' }}>
                <Header setIsAuthenticated={setIsAuthenticated} />
                <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
                    {/* Top Section - Learn English With Us */}
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ p: 3 }}>
                                <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center', fontSize: '1.4rem' }}>
                                    {t('welcome.stepsTitle', 'Aprendre anglès amb nosaltres és tan fàcil com el')} <span style={{ color: '#50A579' }}>1, 2, 3</span>...
                                </Typography>
                                {/* Steps */}
                                <Box sx={{ mt: 4 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography variant="h6" component="span" sx={{ color: '#50A579', fontWeight: 'bold', mr: 2, fontSize: '1.1rem' }}>
                                                1.
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                                                {t('welcome.step1', 'Descobreix el teu nivell d\'anglès.')}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ 
                                            ml: 2, 
                                            bgcolor: '#50A579', 
                                            p: 1.5, 
                                            borderRadius: '50%', 
                                            width: 40, 
                                            height: 40, 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center' 
                                        }}>
                                            <FeedIcon sx={{ color: "white" }} />
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography variant="h6" component="span" sx={{ color: '#50A579', fontWeight: 'bold', mr: 2, fontSize: '1.1rem' }}>
                                                2.
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                                                {t('welcome.step2', 'Tria el curs adequat per a tu')}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ 
                                            ml: 2, 
                                            bgcolor: '#50A579', 
                                            p: 1.5, 
                                            borderRadius: '50%', 
                                            width: 40, 
                                            height: 40, 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center' 
                                        }}>
                                            <SchoolIcon sx={{ color: "white" }} />
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography variant="h6" component="span" sx={{ color: '#50A579', fontWeight: 'bold', mr: 2, fontSize: '1.1rem' }}>
                                                3.
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                                                {t('welcome.step3', "Matricula't i comença a aprendre")}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ 
                                            ml: 2, 
                                            bgcolor: '#50A579', 
                                            p: 1.5, 
                                            borderRadius: '50%', 
                                            width: 40, 
                                            height: 40, 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center' 
                                        }}>
                                            <TouchAppIcon sx={{ color: "white" }} />
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }} id="test-nivell">
                                        <STPButton 
                                            variant="contained" 
                                            sx={{ 
                                                borderRadius: '25px', 
                                                py: 1.2, 
                                                width: '100%',
                                                maxWidth: '90%',
                                                fontSize: '0.9rem'
                                            }}
                                            onClick={handleGoTestNivell}
                                        >
                                            {t('welcome.testButton', 'Fes el test de nivell')}
                                        </STPButton>
                                    </Box>
                                </Box>
                                {/* What makes us different */}
                                <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box 
                                        sx={{ 
                                            width: 120, 
                                            height: 120, 
                                            mr: 2,
                                            overflow: 'hidden',
                                            border: '3px solid #50A579',
                                            flex: 2
                                        }}
                                    >
                                        <img 
                                            src={getImageUrlFromDb("Diferents.png")}
                                            alt={t('welcome.differentAlt', 'What makes us different')} 
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                        />
                                    </Box>
                                    <Box sx={{ flex: 3 }} >
                                        <Typography variant="subtitle1" sx={{ mb: 1, textAlign: 'center', color: '#50A579', fontSize: '0.95rem', fontWeight: 'bold' }}>
                                            {t('welcome.differentTitle', 'Què ens fa diferents?')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ textAlign: 'center', fontSize: '0.85rem' }}>
                                            {t('welcome.differentText', 'Un mètode provat i perfeccionat per ensenyar anglès.')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', fontSize: '0.85rem' }}>
                                            {t('welcome.since', 'Des de 1994')}
                                        </Typography>
                                    </Box>
                                </Box>
                                {/* Com es el nostre mètode */}
                                <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ flex: 3 }} >
                                        <Typography variant="subtitle1" sx={{ mb: 1, textAlign: 'center', color: '#50A579', fontSize: '0.95rem', fontWeight: 'bold' }}>
                                            {t('welcome.methodTitle', 'Com es el nostre mètode?')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ textAlign: 'center', fontSize: '0.85rem' }}>
                                            {t('welcome.methodText', 'En la combinació de classes interactives, pràctiques reals i suport constant.')}
                                        </Typography>
                                    </Box>
                                    <Box 
                                        sx={{ 
                                            width: 120, 
                                            height: 120, 
                                            mr: 2,
                                            overflow: 'hidden',
                                            border: '3px solid #50A579',
                                            flex: 2
                                        }}
                                    >
                                        <img 
                                            src={getImageUrlFromDb("Metode.png")}
                                            alt={t('welcome.methodAlt', 'Our method')} 
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                        {/* Cursos - Columna derecha */}
                        <Grid item xs={12} md={6} id="cursos">
                            <Box sx={{ mt: {xs: 6, md: 0}, mb: 4, maxWidth: {xs: 500, md: '100%'}, mx: 'auto' }}>
                                {/* Cabecera */}
                                <Box 
                                    sx={{ 
                                        p: 1.5, 
                                        bgcolor: '#50A579', 
                                        borderRadius: '8px',
                                        textAlign: 'center',
                                        mb: 0
                                    }}
                                >
                                    <Typography variant="h6" component="h2" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}>
                                        {t('welcome.coursesHeader', 'ELS NOSTRES CURSOS')}
                                    </Typography>
                                </Box>
                                {/* Fondo blanco para los cursos */}
                                <Box sx={{ 
                                    bgcolor: 'white', 
                                    py: 2, 
                                    px: 2,
                                }}>
                                    {welcomeGroups.map((group, index) => (
                                        <CourseItem 
                                            key={group.id}
                                            course={group}
                                            isReverse={index % 2 !== 0}
                                            onClick={handleCourseClick}
                                        />
                                    ))}
                                </Box>

                                {/* Botón de matriculación */}
                                <Box sx={{ textAlign: 'center', mt: 3 }}>
                                    <STPButton 
                                        variant="contained" 
                                        sx={{ 
                                            borderRadius: '25px', 
                                            py: 1.2, 
                                            width: '80%',
                                            maxWidth: '500px',
                                            fontSize: '0.9rem'
                                        }}
                                        onClick={() => window.location.href = "/contact?toMatricular=true"}
                                    >
                                        {t('welcome.enrollNow', "Matricula't ara")}
                                    </STPButton>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
            <CourseDetailDialog open={openModal} onClose={handleCloseModal} group={selectedGroup} />
            {showPromo && randomAnuncio && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        overflow: 'hidden',
                    }}
                >
                    <IconButton
                        onClick={handleClosePromo}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            bgcolor: 'rgba(0, 0, 0, 0.5)',
                            color: 'white',
                            '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.7)',
                            },
                            zIndex: 1,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <img
                        src={randomAnuncio}
                        alt={t('welcome.promoAlt', 'Promoción')}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            display: 'block',
                            opacity: 0.8,
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};

export default Welcome;
